import { tailwindColors } from '@repo/tailwind-config/tailwind.vars'
import { DateFormatter } from '@repo/toolkit/date'
import { StringFormatter } from '@repo/toolkit/string'
import ActionButton from '@repo/ui/action-button'
import Badge from '@repo/ui/badge'
import Box from '@repo/ui/box'
import Button from '@repo/ui/button'
import DataTable, { Column } from '@repo/ui/data-table'
import { DeleteIcon, MessageIcon, PenIcon, PlusIcon } from '@repo/ui/icons'
import Modal from '@repo/ui/modal'
import Typography from '@repo/ui/typography'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { toast } from 'sonner'

import { createTravelExpensesComment } from '../../../api/travel-expenses/travel-expenses-comments/create-travel-expenses-comment'
import { deleteTravelExpensesComments } from '../../../api/travel-expenses/travel-expenses-comments/delete-travel-expenses-comment'
import { getTravelExpensesComments } from '../../../api/travel-expenses/travel-expenses-comments/get-travel-expenses-comments'
import { updateTravelExpensesComment } from '../../../api/travel-expenses/travel-expenses-comments/update-travel-expenses-comment'
import { useModalManager } from '../../../lib/hooks/use-modal-manager'
import { TravelExpensesComment } from '../../../lib/types/models/travel-expenses/travel-expenses-comment'
import { modalButtonConfig } from '../../../lib/utils/buttons-config/validation-buttons-config'
import { showAxiosError } from '../../../lib/utils/functions/misc.functions'
import ActionTableWrapper from '../../elements/action-table-wrapper'
import TravelExpensesCommentForm from './travel-expenses-comment-form'
import { mapTravelExpensesCommentToInput } from './utils'

type Props = {
  id: number
}

const TravelExpensesCommentsTable = ({ id }: Props) => {
  const queryClient = useQueryClient()

  const { modalState, closeModel, selectedData, toggleModal } = useModalManager<
    'create' | 'edit' | 'delete',
    TravelExpensesComment
  >(['create', 'edit', 'delete'])

  const { data: comments } = useQuery({
    queryKey: ['travel-expenses-comments'],
    queryFn: () => getTravelExpensesComments({ affectedStaffId: id }),
    enabled: !!id
  })

  const { mutateAsync: deleteComment, isPending: isLoading } = useMutation({
    mutationFn: deleteTravelExpensesComments,
    onError: showAxiosError,
    onSuccess: () => {
      toast.success('Comment deleted successfully')
      queryClient.invalidateQueries({
        queryKey: ['travel-expenses-comments']
      })
      closeModel('delete')
    }
  })

  const { mutateAsync } = useMutation({
    mutationFn: createTravelExpensesComment,
    onError: showAxiosError,
    onSuccess: () => {
      toast.success('Comment added successfully')
      queryClient.invalidateQueries({
        queryKey: ['travel-expenses-comments']
      })
      closeModel('create')
    }
  })

  const { mutateAsync: updateComment } = useMutation({
    mutationFn: updateTravelExpensesComment,
    onError: showAxiosError,
    onSuccess: () => {
      toast.success('Comment updated successfully')
      queryClient.invalidateQueries({
        queryKey: ['travel-expenses-comments']
      })
      closeModel('edit')
    }
  })

  const columns: Column<TravelExpensesComment>[] = [
    {
      enableSorting: true,
      header: 'Commentaire',
      accessorKey: 'comment',
      cell: ({ row }) => (
        <Typography
          variant="tag"
          color={tailwindColors.black.DEFAULT}
          className="text-wrap px-3 py-4"
        >
          {row.original.comment}
        </Typography>
      )
    },
    {
      enableSorting: true,
      header: 'Commenté par',
      accessorKey: 'commentedBy',
      cell: ({ row }) => (
        <Typography variant="secondary-body" color={tailwindColors.grey.DEFAULT}>
          {`${StringFormatter.capitalize(row.original.createdBy.firstName)} ${row.original.createdBy.lastName.toUpperCase()}`}
        </Typography>
      )
    },
    {
      enableSorting: true,
      header: 'Date & heure',
      accessorKey: 'createdAt',
      cell: ({ row }) => (
        <Badge
          text={`${DateFormatter.DDMMYYYY(row.original.updatedAt)} - ${DateFormatter.HHmm(row.original.updatedAt)}`}
          variant="grey"
        />
      )
    },
    {
      enableSorting: false,
      header: '',
      exportable: false,
      accessorKey: 'id',
      cell: ({ row }) => (
        <div className={'flex gap-2'}>
          <ActionButton
            variant={'grey'}
            icon={PenIcon}
            onClick={() => {
              toggleModal('edit', row.original)
            }}
          />
          <ActionButton
            variant={'danger'}
            icon={DeleteIcon}
            onClick={() => {
              toggleModal('delete', row.original)
            }}
          />
        </div>
      )
    }
  ]

  return (
    <Box className="bg-white rounded-xl border border-grey-light p-0">
      <ActionTableWrapper
        title={'Commentaires'}
        transparent
        actions={[
          <Button
            key={'add'}
            type="button"
            icon={PlusIcon}
            label={'Ajouter un commentaire'}
            variant={'outline'}
            onClick={() => toggleModal('create')}
          />
        ]}
      >
        <DataTable<TravelExpensesComment>
          manualSorting={false}
          columns={columns}
          data={comments?.data ?? []}
          rowCount={comments?.data.length ?? 0}
          paginated={false}
        />

        <Modal
          isOpen={modalState.create}
          title={'Écrire un commentaire'}
          size="medium"
          hideActions
          icon={MessageIcon}
          onCancel={() => closeModel('create')}
        >
          <TravelExpensesCommentForm
            buttonsConfig={modalButtonConfig({
              onCancel: () => closeModel('create'),
              onConfirm: async (values) => {
                await mutateAsync({ ...values, affectedStaffId: id })
              }
            })}
            defaultValues={{}}
          />
        </Modal>

        {selectedData && (
          <Modal
            isOpen={modalState.edit}
            title={'Modifier un commentaire'}
            hideActions
            icon={MessageIcon}
            size="medium"
            onCancel={() => closeModel('edit')}
          >
            <TravelExpensesCommentForm
              defaultValues={mapTravelExpensesCommentToInput(selectedData)}
              buttonsConfig={modalButtonConfig({
                onCancel: () => closeModel('edit'),
                onConfirm: async (values) => {
                  await updateComment({ ...values, affectedStaffId: id, id: selectedData.id })
                }
              })}
            />
          </Modal>
        )}

        {selectedData && (
          <Modal
            actionButtonsOptions={{
              confirmText: 'Supprimer',
              cancelText: 'Fermer',
              onConfirm: async () => {
                await deleteComment({ affectedStaffId: id, ids: [selectedData.id] })
              }
            }}
            onCancel={() => toggleModal('delete')}
            isOpen={modalState.delete}
            title={'Supprimer un commentaire'}
            icon={DeleteIcon}
            isLoading={isLoading}
            variant={'destructive'}
          />
        )}
      </ActionTableWrapper>
    </Box>
  )
}

export default TravelExpensesCommentsTable
